import { ApiError } from "@repo/client/api";
import type { QueryDef } from "@repo/client/data/core";
import {
  "profile-editors.error_generic" as errorGeneric,
  "profile-editors.error_load" as errorLoad,
  "account.owns_populated_groups" as ownsGroupsText,
} from "@repo/i18n";
import { type ReactNode, useCallback, useState } from "react";
import { unavailable, useDataQuery } from "../../../data/query-client";
import { Sheet } from "../../../ui/overlay";
import { FieldError, LoadingBody, SheetActions } from "./parts";

/** What the profile shows once a save landed. */
export interface SavedDetail {
  /** A toast line, when the save deserves one (A2's `notify`). */
  readonly message?: string;
  /** Undo for an append-only write — the weigh-in's `DELETE`. */
  readonly undo?: () => void;
}

/** What `EditorSheet` hands every editor. */
export interface EditorHost {
  readonly describeError: (error: unknown) => string;
  readonly modal?: boolean;
  readonly onClose: () => void;
  readonly onSaved: (detail: SavedDetail) => void;
  readonly open: boolean;
}

/**
 * The sentence for a failed write when the caller passed no
 * `describeError`: the one refusal an editor expects by key (deleting an
 * account that owns a populated group, D153) and a generic line for the rest.
 * T51 passes the app's `useErrorMessage()` once the I18n instance exists,
 * which renders every server key.
 */
export function defaultDescribeError(error: unknown): string {
  if (
    error instanceof ApiError &&
    error.messageKey === "account.owns_populated_groups" &&
    error.params !== undefined
  ) {
    const { groupCount, memberCount, groupNames } = error.params;
    if (
      groupCount !== undefined &&
      memberCount !== undefined &&
      groupNames !== undefined
    ) {
      return ownsGroupsText({ groupCount, groupNames, memberCount });
    }
  }
  return errorGeneric();
}

export interface EditorShellProps {
  children: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  eyebrow?: ReactNode;
  /** A failed save, already a sentence. */
  failure?: string | null;
  /** Replaces the Cancel/Save pair. */
  footer?: ReactNode;
  host: EditorHost;
  /** The section it edits could not be loaded. */
  loadFailed?: boolean;
  /** The section it edits has not loaded yet. */
  loading?: boolean;
  onSave?: () => void;
  pending?: boolean;
  saveLabel?: string;
  title: string;
}

/**
 * T14's `Sheet` with A2's `EditSheet` footer: Cancel and the one action.
 * The drag starts from the header only (the Sheet's default `handle`), so a
 * long form scrolls.
 */
export function EditorShell({
  host,
  title,
  eyebrow,
  children,
  saveLabel,
  onSave,
  disabled,
  pending,
  danger,
  failure,
  footer,
  loading,
  loadFailed,
}: EditorShellProps) {
  const { onClose } = host;
  const openChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClose();
      }
    },
    [onClose]
  );
  const blocked = (loading ?? false) || (loadFailed ?? false);
  const actions = footer ?? (
    <SheetActions
      danger={danger}
      disabled={blocked || (disabled ?? false) || onSave === undefined}
      onCancel={onClose}
      onSave={onSave ?? onClose}
      pending={pending}
      saveLabel={saveLabel}
    />
  );
  return (
    <Sheet
      eyebrow={eyebrow}
      footer={actions}
      modal={host.modal}
      onOpenChange={openChange}
      open={host.open}
      title={title}
    >
      {loading === true ? <LoadingBody /> : null}
      {loadFailed === true && loading !== true ? (
        <FieldError>{errorLoad()}</FieldError>
      ) : null}
      {blocked ? null : <div>{children}</div>}
      {failure ? <FieldError>{failure}</FieldError> : null}
    </Sheet>
  );
}

/**
 * A section read for an editor. A `404` is an answer — the section was never
 * written — so it resolves to `null` and the editor opens empty; any other
 * failure is `loadFailed`.
 */
export function useSectionRead<T>(def: QueryDef<T>) {
  const query = useDataQuery(def);
  const missing = query.error instanceof ApiError && query.error.status === 404;
  // `unavailable`: offline with nothing cached is a failed load, not a
  // spinner that never ends (`D194`).
  const failed = unavailable(query);
  return {
    data: missing ? null : (query.data ?? null),
    loadFailed: failed && !missing,
    loading: query.isPending && !failed,
  };
}

/** What a save is told back: the answer, or why there is none. */
export interface MutateCallbacks<O> {
  onError?(error: Error): void;
  onSuccess?(output: O): void;
}

/** The one call a save needs — an outbox write (`use-editor-mutation.ts`). */
export interface Mutator<P, O> {
  readonly isPending: boolean;
  mutate(payload: P, options?: MutateCallbacks<O>): void;
}

/**
 * Save, then close. Every section's write is an outbox write since `D194`:
 * the cache moves at once, the write is on disk before `onSuccess`, and the
 * sheet closes on the tap — online or not, with no wait on the server. A
 * refusal the server sends later lands in the outbox's quarantine, where it
 * is shown and kept until retried or discarded; only a write the queue itself
 * refuses (a programming error) is shown here.
 */
export function useSaveFlow<P, O>(mutation: Mutator<P, O>, host: EditorHost) {
  const [failure, setFailure] = useState<string | null>(null);
  const { mutate } = mutation;
  const { describeError, onClose, onSaved } = host;
  const save = useCallback(
    (payload: P, detail?: (output: O) => SavedDetail) => {
      setFailure(null);
      mutate(payload, {
        onError: (error) => setFailure(describeError(error)),
        onSuccess: (output) => {
          onSaved(detail?.(output) ?? {});
          onClose();
        },
      });
    },
    [mutate, describeError, onClose, onSaved]
  );
  return { failure, pending: mutation.isPending, save, setFailure };
}
